/**
 * RP2 — reusable IN-PLACE resolve dialog.
 *
 * Generalizes the canonical SED-amendment construction that
 * SEDVariableBrowser.submitAmendment (SEDVariableBrowser.tsx:699-744) and the
 * admin CseConflictResolveDialog already do, into ONE dialog that:
 *
 *   1. Takes a `draft` describing the value source (sedId, dotted variablePath,
 *      label, domain, old value, suggested new value, unit).
 *   2. Lets the user pick / type the new value + a REQUIRED rationale + a
 *      source (the override-with-rationale escape hatch — the
 *      precedence-resolution rule: NEVER hard-block; always offer a resolution
 *      dialog with a non-blocking exit).
 *   3. Constructs the canonical `SEDAmendment` (same coercion + validation as
 *      SEDVariableBrowser) and routes it through the injected `onResolve`, which
 *      the host wires to the ONE persist path — the `saveSEDAmendment` store
 *      action. NO new write path.
 *
 * The dialog is value-source agnostic: the Decision tab's blocker list
 * (`DecisionBlockerList`, per-blocker remediation), SiteAuditTab
 * (internal-number reconcile) and FeasibilityRerunPanel (data-gap manual
 * input) all construct a `draft` and reuse this component.
 *
 * Moved from `pathToGo/` when the Path to GO tab code was deleted (Path to GO
 * removal, stage 3). Behaviour unchanged. The hand-rolled case +
 * letter-spacing chrome was tokenised: the field labels keep `type-meta`,
 * which already sets both (identical render); the title is `type-section` with
 * its size and colour kept by `text-sm text-foreground` (same weight, case and
 * 0.1em tracking as before).
 *
 * GUARD: the new value is coerced to the old value's type (number / boolean /
 * string). `applySingleAmendmentToSed` independently enforces a deep-enough
 * dotted path + schema-resolution, so a mis-targeted slot fails the apply with a
 * surfaced error rather than corrupting the SED. Power-basis / intent are NOT
 * amended through this SED path (they go through `updateSiteProjectIntent`), so
 * this dialog cannot corrupt the power-basis / intent model.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '../../ui/button';
import type { AmendmentSource, SaveAmendmentResult, SEDAmendment } from '../../../types/sedAmendments';

/** The value source to amend in-place. */
export interface ResolveAmendmentDraft {
  /** SED this amendment targets. */
  sedId: string;
  /** Current SED version (stamped on the amendment record). */
  sedVersion: number;
  /** Dotted variablePath, e.g. "climate.data.avgTemp". Must be deep enough. */
  variablePath: string;
  /** Human-readable label for the value being amended. */
  variableLabel: string;
  /** Evidence section / domain ("climate", "grid", …). */
  domain: string;
  /** The current value (used for the type-coercion guard + the "Was:" display). */
  oldValue: string | number | boolean | null;
  /** Optional pre-filled new value (e.g. the chosen canonical value). */
  suggestedValue?: string | number | boolean | null;
  /** Optional unit chip. */
  unit?: string;
  /** Optional candidate values to offer as radio choices (reconcile case). */
  candidateValues?: readonly string[];
  /** Optional default rationale (e.g. "Internal-number reconcile"). */
  defaultReason?: string;
  /** Optional issue classification stamped on the amendment. */
  issueType?: SEDAmendment['issueType'];
}

export interface ResolveAmendmentDialogProps {
  open: boolean;
  draft: ResolveAmendmentDraft | null;
  /** The ONE persist path — host wires this to `saveSEDAmendment`. */
  onResolve: (amendment: SEDAmendment) => Promise<SaveAmendmentResult>;
  onClose: () => void;
  /** Called after a successful apply (host reloads SED + clears the finding). */
  onResolved?: (amendment: SEDAmendment, result: { ok: true; applied: boolean }) => void;
  /** Toast bridge (optional). */
  toast?: (msg: string) => void;
}

const SOURCE_TYPES: { value: AmendmentSource['type']; label: string }[] = [
  { value: 'expert-judgment', label: 'Expert judgment' },
  { value: 'document', label: 'Supporting document' },
  { value: 'external-link', label: 'External source (URL)' },
  { value: 'site-visit', label: 'Site visit' },
];

function stringifyValue(v: string | number | boolean | null | undefined): string {
  if (v == null) return '';
  return String(v);
}

export const ResolveAmendmentDialog: React.FC<ResolveAmendmentDialogProps> = ({
  open,
  draft,
  onResolve,
  onClose,
  onResolved,
  toast,
}) => {
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [sourceType, setSourceType] = useState<AmendmentSource['type']>('expert-judgment');
  const [sourceLabel, setSourceLabel] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasInteracted, setHasInteracted] = useState(false);

  // Seed the form when the dialog opens for a fresh draft.
  useEffect(() => {
    if (!open || !draft) return;
    setValue(
      stringifyValue(
        draft.suggestedValue ?? draft.candidateValues?.[0] ?? draft.oldValue,
      ),
    );
    setReason(draft.defaultReason ?? '');
    setSourceType('expert-judgment');
    setSourceLabel('');
    setSourceUrl('');
    setSubmitting(false);
    setError(null);
    setHasInteracted(false);
  }, [open, draft]);

  // Mirror SEDVariableBrowser's required-field validation so the audit trail
  // always carries a rationale + a source (the override-with-rationale hatch).
  const blockingFields = useMemo(() => {
    const blockers: string[] = [];
    if (reason.trim().length === 0) blockers.push('Reason for change');
    if (sourceLabel.trim().length === 0) blockers.push('Source description');
    if (sourceType === 'external-link' && sourceUrl.trim().length === 0) {
      blockers.push('Source URL');
    }
    return blockers;
  }, [reason, sourceLabel, sourceType, sourceUrl]);

  const isSubmittable = !submitting && blockingFields.length === 0;
  const blockerMessage =
    blockingFields.length === 0
      ? null
      : blockingFields.length === 1
        ? `${blockingFields[0]} is required.`
        : `Required: ${blockingFields.join(', ')}.`;

  if (!open || !draft) return null;

  const handleApply = async () => {
    if (!isSubmittable) {
      setHasInteracted(true);
      return;
    }

    const auth = (await import('../../../firebase')).auth;
    const user = auth.currentUser;
    if (!user) {
      setError('Not signed in');
      return;
    }

    // GUARD — coerce the typed value to the old value's type so a numeric slot
    // never receives a string (mirrors SEDVariableBrowser.submitAmendment).
    let newValue: string | number | boolean | null = value;
    if (typeof draft.oldValue === 'number') {
      const parsed = parseFloat(value);
      if (!Number.isFinite(parsed)) {
        toast?.('Invalid number — please enter a numeric value');
        setError('Invalid number — please enter a numeric value');
        return;
      }
      newValue = parsed;
    }
    if (typeof draft.oldValue === 'boolean') newValue = value === 'true';

    const amendment: SEDAmendment = {
      id: `amend-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      sedId: draft.sedId,
      sedVersion: draft.sedVersion,
      variablePath: draft.variablePath,
      variableLabel: draft.variableLabel,
      domain: draft.domain,
      oldValue: draft.oldValue,
      newValue,
      unit: draft.unit,
      reason,
      source: {
        type: sourceType,
        label: sourceLabel || sourceType.replace('-', ' '),
        url: sourceType === 'external-link' ? sourceUrl : undefined,
      },
      createdBy: user.uid,
      createdByEmail: user.email || '',
      createdAt: new Date().toISOString(),
      status: 'pending',
      ...(draft.issueType ? { issueType: draft.issueType } : {}),
    };

    setSubmitting(true);
    setError(null);
    try {
      const result = await onResolve(amendment);
      if (!result.ok) {
        setError(result.error);
        toast?.(`Failed to resolve: ${result.error}`);
        return;
      }
      toast?.(
        result.applied
          ? 'Resolved and applied to site evidence.'
          : 'Resolution recorded (pending review).',
      );
      onResolved?.(amendment, result);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const candidates = draft.candidateValues ?? [];

  return (
    <div
      className="fixed inset-0 z-[56] flex items-center justify-center bg-black/50"
      onClick={onClose}
      data-testid="resolve-amendment-dialog"
    >
      <div
        className="bg-background w-[calc(100vw-2rem)] max-w-[560px] max-h-[85vh] overflow-y-auto shadow-2xl border border-border rounded-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" aria-hidden />
            <div className="min-w-0">
              <h2 className="type-section text-sm text-foreground">
                Resolve at the value source
              </h2>
              <p className="text-[11px] text-text-secondary mt-1 leading-relaxed">
                Set the authoritative value for{' '}
                <span className="font-mono text-text-primary">{draft.variableLabel}</span>. This
                writes through to site evidence and moves the verdict — no navigation away.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-text-muted hover:text-text-primary shrink-0"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="text-[11px] font-mono text-text-muted">
            {draft.variablePath}
            {draft.oldValue != null && (
              <>
                <span className="text-text-muted"> · was </span>
                <span className="text-text-secondary">{stringifyValue(draft.oldValue)}</span>
                {draft.unit ? <span className="text-text-muted"> {draft.unit}</span> : null}
              </>
            )}
          </div>

          {/* New value — radio candidates when present, else a free input. */}
          <label className="block space-y-1.5">
            <span className="type-meta text-text-secondary font-bold">
              New value{draft.unit ? ` (${draft.unit})` : ''}
            </span>
            {candidates.length > 0 ? (
              <div className="flex flex-col gap-1.5">
                {candidates.map((c) => (
                  <label key={c} className="flex items-center gap-2 text-[12px] cursor-pointer">
                    <input
                      type="radio"
                      name="resolve-amendment-value"
                      checked={value === c}
                      onChange={() => setValue(c)}
                      data-testid={`resolve-amendment-candidate-${c}`}
                    />
                    <span className="font-mono">{c}</span>
                  </label>
                ))}
              </div>
            ) : (
              <input
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[13px] text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                data-testid="resolve-amendment-value-input"
              />
            )}
          </label>

          {/* Rationale — REQUIRED (override-with-rationale escape hatch). */}
          <label className="block space-y-1.5">
            <span className="type-meta text-text-secondary font-bold">
              Reason for change
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="Why is this the authoritative value? (required)"
              className={`w-full rounded-md border bg-background px-2 py-1.5 text-[13px] text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                hasInteracted && reason.trim().length === 0 ? 'border-danger' : 'border-border'
              }`}
              data-testid="resolve-amendment-reason"
            />
          </label>

          {/* Source — provenance the regenerator / reviewer can cite. */}
          <div className="grid grid-cols-2 gap-2">
            <label className="block space-y-1.5">
              <span className="type-meta text-text-secondary font-bold">
                Source type
              </span>
              <select
                value={sourceType}
                onChange={(e) => setSourceType(e.target.value as AmendmentSource['type'])}
                className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[13px] text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                data-testid="resolve-amendment-source-type"
              >
                {SOURCE_TYPES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block space-y-1.5">
              <span className="type-meta text-text-secondary font-bold">
                Source description
              </span>
              <input
                type="text"
                value={sourceLabel}
                onChange={(e) => setSourceLabel(e.target.value)}
                placeholder="e.g. Geotech report, Q2 2026"
                className={`w-full rounded-md border bg-background px-2 py-1.5 text-[13px] text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                  hasInteracted && sourceLabel.trim().length === 0 ? 'border-danger' : 'border-border'
                }`}
                data-testid="resolve-amendment-source-label"
              />
            </label>
          </div>

          {sourceType === 'external-link' && (
            <label className="block space-y-1.5">
              <span className="type-meta text-text-secondary font-bold">
                Source URL
              </span>
              <input
                type="url"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                placeholder="https://…"
                className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-[13px] text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                data-testid="resolve-amendment-source-url"
              />
            </label>
          )}

          {(blockerMessage && hasInteracted) || error ? (
            <p className="type-meta text-danger" role="alert" data-testid="resolve-amendment-error">
              {error ?? blockerMessage}
            </p>
          ) : null}
        </div>

        <div className="px-5 py-4 border-t border-border flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => void handleApply()}
            disabled={!isSubmittable}
            data-testid="resolve-amendment-apply"
          >
            {submitting ? 'Resolving…' : 'Resolve & apply'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ResolveAmendmentDialog;
